/**
 * Email service for Correly.
 *
 * Sends transactional emails via a Supabase Edge Function
 * that uses Resend (resend.com) for delivery.
 *
 * Setup:
 *   1. Create a free Resend account at resend.com
 *   2. Get your API key from the dashboard
 *   3. Set it as a Supabase secret:
 *      supabase secrets set RESEND_API_KEY=re_your_api_key_here
 *   4. Deploy the Edge Function:
 *      supabase functions deploy send-invite-email
 */

import { supabase } from './supabase';

/**
 * Send an invite email to a newly added client.
 *
 * @param {object} params
 * @param {string} params.clientName - The client's full name
 * @param {string} params.clientEmail - The client's email address
 * @param {string} params.practitionerName - The practitioner's name
 * @param {string} params.inviteCode - The generated invite code
 * @param {string} params.inviteLink - The full invite URL
 * @returns {{ success: boolean, error?: string }}
 */
export async function sendInviteEmail({
  clientName,
  clientEmail,
  practitionerName,
  inviteCode,
  inviteLink,
}) {
  try {
    const { data, error } = await supabase.functions.invoke('send-invite-email', {
      body: {
        clientName,
        clientEmail,
        practitionerName,
        inviteCode,
        inviteLink,
      },
    });

    if (error) {
      console.error('Failed to send invite email:', error);
      return { success: false, error: error.message };
    }

    if (data?.error) {
      console.error('Email service error:', data.error);
      return { success: false, error: data.error };
    }

    return { success: true };
  } catch (err) {
    console.error('Email send error:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Check if the email service is likely configured.
 * This is a soft check — the real validation happens server-side.
 */
export function isEmailConfigured() {
  // The Edge Function handles the actual check for RESEND_API_KEY.
  // This just checks that Supabase is available.
  return !!supabase;
}
